import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { lazy, useId } from 'react';
import { resolveSlugSeo, Seo } from '../-components/seo';
import { Layout, Paragraph, Title } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Image } from '../-components/image';
import { RevealOnScroll } from '@worldcoo/ui/components/reveal-on-scroll';
import { trpc } from '@/lib/trpc';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { slugs } from './about-us.meta';
import { wwwLocales } from './-components/utils';
import { useSuspenseQuery } from '@tanstack/react-query';

const Newsletter = lazy(() => import('./-components/newsletter').then((m) => ({ default: m.Newsletter })));

export const Route = createFileRoute('/www/about-us')({
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(
			trpc.portal.getWorldcooGlobalStats.queryOptions(undefined, { staleTime: 60 * 60 * 1000 }),
		);
	},
	component: Component,
});

function Component() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { baseUrl, locale, hasLocalePrefix } = Route.useRouteContext();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	const formattedTotalRaisedM = Math.floor(stats.totalRaised.amount / 1000000);
	const formattedDonationsM = Math.floor(stats.totalDonations / 1000000);
	const formattedPartnerships = formatNumber(language, stats.partnershipAgreements, 0);
	const { canonicalUrl, alternates } = resolveSlugSeo(hasLocalePrefix, baseUrl, locale, wwwLocales, slugs);

	return (
		<Layout>
			<Header />
			<Seo
				title={t`About us - Worldcoo`}
				description={t`Worldcoo was created in 2012 in Barcelona as an online tool to connect companies with social causes in a free and efficient way.`}
				canonicalUrl={canonicalUrl}
				locale={locale}
				alternateLocales={[...wwwLocales]}
				alternates={alternates}
			/>

			<section className="bg-hero flex flex-col items-center gap-8 px-8 py-16 text-center md:py-24">
				<Title>{t`A story with social impact`}</Title>
				<Paragraph>{t`What if we could make a micro-donation every time we use our card?`}</Paragraph>
				<Image
					src="/about-us/hero.jpg"
					height={597}
					aspectRatio={896 / 597}
					alt={t`Worldcoo team group photo — staff standing and smiling in front of a presentation screen in the office`}
					priority
					className="mt-8 rounded-lg object-cover"
				/>
			</section>

			<section className="mx-auto max-w-6xl px-8 py-16">
				<Story
					title={t`Connecting social causes`}
					description={t`Worldcoo was created in 2012 in Barcelona as an online tool to connect companies with social causes in a free and efficient way. Since then, we've raised more than ${formattedTotalRaisedM} million euros thanks to the ${formattedDonationsM} million-plus donations that have been made through our channels.`}
					imageSrc="/about-us/item1.png"
					alt={t`Smiling child on outdoor steps giving two thumbs up, representing people helped by Worldcoo`}
					side="left"
				/>
				<Story
					title={t`Launch of Charity Rounding up`}
					description={t`After several years focused on the online environment, in 2017 we launched Charity Rounding Up, a new fundraising channel for shoppers to add a few cents to their purchase and make a donation every time they pay with a card at brick-and-mortar stores.`}
					imageSrc="/about-us/item2.png"
					alt={t`Close-up of a card payment terminal with a shopper's hand, illustrating charity rounding up at checkout`}
					side="right"
				/>
				<Story
					title={t`We process 100,000 donations a day`}
					description={t`In 2023, we joined forces with Glovo and Delivery Hero. And in July of the same year, the 5,000-plus points of sale with Charity Rounding Up processed 100,000 donations a day.`}
					imageSrc="/about-us/item3.png"
					alt={t`Young child standing outdoors looking at the camera, symbolizing communities supported by donations`}
					side="left"
				/>
				<Story
					title={t`Standard platform for online donations`}
					description={t`Our strict social and environmental performance standards, as well as our public transparency and legal accountability, have been certified by B Corp since 2015. And the European Union has named Worldcoo as the standard platform for online donations.`}
					imageSrc="/about-us/item4.png"
					alt={t`Person using a laptop at a desk, representing Worldcoo's online donation platform and digital services`}
					side="right"
				/>
				<Story
					title={t`International agreements`}
					description={t`We currently have ${formattedPartnerships} partnership agreements with social entities around the world, funding projects in more than 40 countries. Our commitment is to make further headway, continuously advocating innovation, transparency and social commitment.`}
					imageSrc="/about-us/item5.png"
					alt={t`Graphic illustrating international partnerships — stylized map or globe representing projects across multiple countries`}
					side="left"
				/>
			</section>
			<RevealOnScroll>
				<Newsletter />
			</RevealOnScroll>
			<Footer />
		</Layout>
	);
}

function Story(props: { imageSrc: string; title: string; description: string; alt: string; side: 'left' | 'right' }) {
	const id = useId();

	return (
		<article aria-labelledby={'story-' + id} className="flex flex-col gap-8 py-8 md:flex-row md:gap-16">
			<div className={`w-full md:w-1/2 ${props.side === 'left' ? 'md:order-1' : 'md:order-2'}`}>
				<Image
					src={props.imageSrc}
					alt={props.alt}
					height={380}
					aspectRatio={522 / 380}
					className="mx-auto rounded-lg"
				/>
			</div>

			{/* Text content */}
			<div className={`w-full py-8 md:w-1/2 ${props.side === 'left' ? 'md:order-2' : 'md:order-1'}`}>
				<h2 id={'story-' + id} className="mb-4 text-2xl font-bold md:text-3xl">
					{props.title}
				</h2>
				<p className="leading-relaxed">{props.description}</p>
			</div>
		</article>
	);
}
